/**
 * useDuplicateRows — the admin-only "Duplikate" audit view (DMS Part 2, P2-12,
 * dms/spec-part2-stefan-delta.md §2.4).
 *
 * live mode → TWO parallel fetches (the "if the API exposes one" branch of P2-12's
 * contract note, since it now does — the P2-12 wave added `GET /documents/duplicates`
 * alongside `GET /documents?include_duplicates=true`, mirroring P2-10's `DuplicateInfoDto`):
 *   1. `GET /documents?include_duplicates=true&include_inactive=true` — the SAME broad
 *      window `useDocumentRows` fetches, but admin-opted-in, so it returns BOTH the
 *      productive stock AND the flagged duplicates. Used ONLY for display fields
 *      (title/bezug/quelle/doc_date/size_bytes) — for the duplicate rows themselves
 *      AND for resolving each duplicate's ORIGINAL display title.
 *   2. `GET /documents/duplicates` — the `document_dedup` audit trail itself
 *      (document_id/matched_document_id/method/score/detected_at). This is the
 *      COMPLETE, authoritative list of duplicates (unbounded, unlike the 500-row
 *      list-window cap) — every row iterated here, never derived from (1) alone.
 *
 * `enabled` (from the caller — `useEffectivePermissions().isWildcard`, mirroring the
 * server's `@Roles('admin')` gate) — a non-admin caller never fires either fetch, a
 * defense-in-depth belt alongside the "Duplikate" pill simply not being rendered.
 *
 * mock mode → `DUPLICATE_DOCUMENTS_MOCK` (the "list" leg) + `DUPLICATES_MOCK` (the
 * "duplicates" leg), same two-source shape as live.
 *
 * [Source: apps/web/src/features/documents/hooks/useDocumentRows.ts] (live/mock hook precedent)
 * [Source: packages/shared/src/document.ts#DuplicateInfoDto]
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §2.4, P2-12]
 */
import { useQuery } from '@tanstack/react-query';
import type { DocumentDedupMethod, DocumentListItemDto, DuplicateInfoDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchDocumentList, fetchDuplicates } from '../api/documents-api';
import { DOCUMENTS_MOCK, DUPLICATE_DOCUMENTS_MOCK, DUPLICATES_MOCK } from '../mock';

/** One row of the admin "Duplikate" table — a duplicate document + its match info. */
export interface DuplicateRow {
  id: string;
  title: string;
  bezug: string | null;
  quelle: string;
  doc_date: string;
  size_bytes: number | null;
  original_id: string;
  /** `null` when the original fell outside the fetched list window (rare — display falls back to the id). */
  original_title: string | null;
  method: DocumentDedupMethod;
  score: string | null;
  detected_at: string;
}

/**
 * Iterates `duplicates` (the authoritative `document_dedup` rows — NOT the list
 * window) and enriches each with display fields looked up from `allDocs`. A
 * duplicate/original missing from `allDocs` (list-window edge case) degrades
 * gracefully (title falls back to the raw id) rather than dropping the row.
 */
function buildDuplicateRows(
  allDocs: readonly DocumentListItemDto[],
  duplicates: readonly DuplicateInfoDto[],
): DuplicateRow[] {
  const byId = new Map(allDocs.map((d) => [d.id, d]));

  return duplicates.map((info) => {
    const doc = byId.get(info.document_id);
    const original = byId.get(info.matched_document_id);
    return {
      id: info.document_id,
      title: doc?.title ?? info.document_id,
      bezug: doc?.bezug ?? null,
      quelle: doc?.quelle ?? '-',
      doc_date: doc?.doc_date ?? info.detected_at,
      size_bytes: doc?.size_bytes ?? null,
      original_id: info.matched_document_id,
      original_title: original?.title ?? null,
      method: info.method,
      score: info.score,
      detected_at: info.detected_at,
    };
  });
}

/** Mock-mode fixture, precomputed once (module scope) — the `initialData` value below. */
const MOCK_ROWS = buildDuplicateRows([...DOCUMENTS_MOCK, ...DUPLICATE_DOCUMENTS_MOCK], DUPLICATES_MOCK);

export function useDuplicateRows(enabled: boolean) {
  const live = isLiveData();

  const query = useQuery({
    queryKey: qk.documents({ include_duplicates: true, mode: live ? 'live' : 'mock' }),
    queryFn: async () => {
      if (!live) return MOCK_ROWS;
      const [listRes, duplicates] = await Promise.all([
        fetchDocumentList({ include_duplicates: true, include_inactive: true }),
        fetchDuplicates(),
      ]);
      return buildDuplicateRows(listRes.items, duplicates);
    },
    enabled,
    // Mirrors useDocumentRows/useEffectivePermissions: mock mode resolves synchronously
    // via `initialData` (no pending→success transition/extra render) rather than going
    // through a real async tick, exactly like every other mock-mode hook in this app.
    ...(live ? {} : { initialData: MOCK_ROWS, staleTime: Infinity }),
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
